import { ApiResponse } from './userApi';
import api, { rawFetchWithAuth } from './apiClient';
import { config } from 'config';

export interface ContractorInitialCourse {
  _id?: string;
  name: string;
  completionDate: string;
  certificateUrl?: string;
  talentLMSCourseId?: string;
}

export interface ContractorAdditionalCourse {
  _id?: string;
  name: string;
  completionDate: string;
  expiryDate?: string;
  certificateUrl?: string;
  talentLMSCourseId?: string;
}

export interface ContractorFormData {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  cedula: string;
  email?: string;
  phone?: string;
  companyId: string;
  status?: 'active' | 'inactive' | 'suspended';
  polizaINS?: {
    number: string;
    expiryDate: string;
    documentUrl?: string;
  };
  ordenPatronal?: {
    number?: string;
    expiryDate?: string;
    documentUrl?: string;
  };
  supervisorId?: string;
}

export interface Contractor {
  _id: string;
  firstName: string;
  lastName: string;
  fullName?: string; // Optional fallback
  cedula: string;
  email?: string;
  phone?: string;
  status: 'active' | 'inactive' | 'suspended' | 'activo' | 'inactivo' | 'baja'; // Support both English and Spanish
  company?: {
    _id: string;
    name: string;
    industry?: string;
    userCount?: number;
    settings?: {
      notificationDays: number[];
      requiredCourses: {
        name: string;
        isInitial: boolean;
        _id: string;
      }[];
    };
  };
  polizaINS?: {
    number: string;
    expiryDate: string;
    documentUrl?: string;
  };
  ordenPatronal?: {
    number?: string;
    expiryDate?: string;
    documentUrl?: string;
  };
  initialCourses?: ContractorInitialCourse[];
  additionalCourses?: ContractorAdditionalCourse[];
  certifications?: {
    name: string;
    issuedDate: string;
    expiryDate: string;
    status: string;
    _id: string;
  }[];
  courses?: {
    courseId: string;
    courseName: string;
    completedDate: string;
    score: number;
    status: string;
    _id: string;
  }[];
  supervisor?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  reviewStats?: {
    averageRating: number;
    totalReviews: number;
    lastReviewDate?: string;
    wouldHireAgainPercentage: number;
  };
  expiringItems?: unknown[];
  deactivationDate?: string;
  deactivationReason?: string;
  /** Pre-resolved profile picture URL (CDN/proxy). */
  profilePictureUrl?: string;
  /** Storage key for the profile picture (falls back to building the URL client-side). */
  profilePicture?: string;
  /** Aggregate verification rollup surfaced by `contractorApi.getById`. */
  verificationSummary?: {
    globalCompliance?: 'compliant' | 'partial' | 'non_compliant';
    details?: Array<{
      companyId?: string;
      companyName?: string;
      complianceStatus?: 'compliant' | 'partial' | 'non_compliant';
      verificationsTotal?: number;
      verificationsCompleted?: number;
      verifications?: Array<{
        name?: string;
        isRequired?: boolean;
        status?: string;
      }>;
    }>;
  };
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface ContractorFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  companyId?: string;
}

export const contractorApi = {
  // Get all contractors
  getAll: async (filters?: ContractorFilters): Promise<ApiResponse<Contractor[]>> => {
    return api.get('/contractors', filters as Record<string, any>);
  },

  // Get contractor by ID
  getById: async (id: string): Promise<ApiResponse<Contractor>> => {
    return api.get(`/contractors/${id}`);
  },

  // Create new contractor
  create: async (data: ContractorFormData): Promise<ApiResponse<Contractor>> => {
    return api.post('/contractors', data);
  },

  // Update contractor
  update: async (id: string, data: Partial<ContractorFormData>): Promise<ApiResponse<Contractor>> => {
    return api.put(`/contractors/${id}`, data);
  },

  // Delete contractor
  delete: async (id: string, reason?: string): Promise<ApiResponse<any>> => {
    return api.delete(`/contractors/${id}`, { data: reason ? { reason } : {} });
  },

  // Add initial course to contractor
  addInitialCourse: async (id: string, course: ContractorInitialCourse): Promise<ApiResponse<any>> => {
    return api.post(`/contractors/${id}/initial-courses`, course);
  },

  // Add additional course to contractor
  addAdditionalCourse: async (id: string, course: ContractorAdditionalCourse): Promise<ApiResponse<any>> => {
    return api.post(`/contractors/${id}/additional-courses`, course);
  },

  // Update initial course
  updateInitialCourse: async (id: string, courseId: string, course: Partial<ContractorInitialCourse>): Promise<ApiResponse<any>> => {
    return api.put(`/contractors/${id}/initial-courses/${courseId}`, course);
  },

  // Update additional course
  updateAdditionalCourse: async (id: string, courseId: string, course: Partial<ContractorAdditionalCourse>): Promise<ApiResponse<any>> => {
    return api.put(`/contractors/${id}/additional-courses/${courseId}`, course);
  },

  // Delete initial course
  deleteInitialCourse: async (id: string, courseId: string): Promise<ApiResponse<any>> => {
    return api.delete(`/contractors/${id}/initial-courses/${courseId}`);
  },

  // Delete additional course
  deleteAdditionalCourse: async (id: string, courseId: string): Promise<ApiResponse<any>> => {
    return api.delete(`/contractors/${id}/additional-courses/${courseId}`);
  },

  // Get contractor evaluations
  getEvaluations: async (id: string): Promise<ApiResponse<any>> => {
    return api.get(`/contractors/${id}/evaluations`);
  },

  // Add new evaluation
  addEvaluation: async (id: string, evaluation: {
    scores: {
      safety: number;
      quality: number;
      timeliness: number;
      communication: number;
    };
    comments?: string;
  }): Promise<ApiResponse<any>> => {
    return api.post(`/contractors/${id}/evaluate`, evaluation);
  },


  // Get terms acceptance status
  getTermsAcceptance: async (id: string): Promise<ApiResponse<any>> => {
    return api.get(`/contractors/${id}/terms`);
  },

  // Get verification documents
  getVerificationDocuments: async (id: string): Promise<ApiResponse<any>> => {
    return api.get(`/contractors/${id}/verification-documents`);
  },

  // Export contractors (raw fetch for blob response)
  exportContractors: async (filters: ContractorFilters & { format?: 'csv' | 'excel' }): Promise<Blob> => {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.companyId) params.append('companyId', filters.companyId);
    params.append('format', filters.format || 'excel');

    const response = await rawFetchWithAuth(
      `${config.apiUrl}/contractors/export?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error('Error al exportar los contratistas');
    }

    return response.blob();
  }
};
